<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="selection-bar" :class="{ 'is-mobile': isSmallScreen }">
    <!-- Desktop Layout -->
    <template v-if="!isSmallScreen">
      <div class="selection-info">
        <span class="selection-text">
          {{
            $t("selection.count", {
              selected: selectedCount,
              total: totalItemsComputed,
            })
          }}
        </span>
        <span
          v-if="showSelectAll"
          class="select-all-link"
          @click="selectAllNodes"
        >
          {{ $t("selection.selectAll", { total: totalItemsComputed }) }}
        </span>
        <span
          v-else-if="isSelectionRemovalVisible || isAllSelected"
          class="select-all-link"
          @click="clearSelection"
        >
          {{ $t("selection.clearSelection") }}
        </span>
      </div>

      <slot
        name="actions"
        :nodes="nodes"
        :files="files"
        :folders="folders"
        :selected-count="selectedCount"
      >
        <div class="selection-actions">
          <template v-for="(item, index) in inlineActions" :key="index">
            <div v-if="item._groupSeparator" class="action-separator" />
            <PrimeButton
              v-else
              v-tooltip.top="resolveStringOrFn(item.tooltip, actionContext)"
              :icon="resolveStringOrFn(item.icon, actionContext)"
              :disabled="resolveDisabled(item)"
              @click="item.handler(actionContext)"
              rounded
              text
              :severity="item.severity || 'secondary'"
            />
          </template>

          <!-- More actions menu button -->
          <template v-if="menuActions.length > 0">
            <div class="action-separator" />
            <PrimeButton
              v-tooltip.top="$t('moreActions')"
              icon="ri-more-2-line"
              @click="toggleMoreMenu"
              rounded
              text
              severity="secondary"
            />
          </template>
        </div>
      </slot>
    </template>

    <!-- Mobile Layout -->
    <template v-else>
      <div class="mobile-header">
        <div class="mobile-selection-info">
          <i
            aria-hidden="true"
            class="ri-checkbox-multiple-line selection-icon"
          />
          <span class="selection-text">
            {{
              $t("selection.mobileCount", {
                selected: selectedCount,
                total: totalItemsComputed,
              })
            }}
          </span>
        </div>
        <div class="mobile-selection-actions">
          <span
            v-if="showSelectAll"
            class="select-all-link"
            @click="selectAllNodes"
          >
            {{ $t("selection.selectAllShort") }}
          </span>
          <span
            v-else-if="isSelectionRemovalVisible || isAllSelected"
            class="select-all-link"
            @click="clearSelection"
          >
            {{ $t("selection.clear") }}
          </span>
        </div>
      </div>

      <slot
        name="actions"
        :nodes="nodes"
        :files="files"
        :folders="folders"
        :selected-count="selectedCount"
      >
        <div class="mobile-actions">
          <!-- On mobile, only show the more-actions menu button -->
          <PrimeButton
            v-if="allMobileMenuActions.length > 0"
            v-tooltip.top="$t('moreActions')"
            icon="ri-more-2-line"
            @click="toggleMoreMenu"
            rounded
            text
            severity="secondary"
          />
        </div>
      </slot>
    </template>

    <!-- More actions menu -->
    <ActionMenu ref="moreMenu" :action-menu-items="menuActionItems" />
  </div>
</template>

<script>
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { useSelectionStore } from "@/stores/selection";
import { useNavigationStore } from "@/stores/navigation";
import { useScreenStore } from "@/stores/screen";
import alfrescoNodeService from "@/services/AlfrescoNodeService";
import ActionMenu from "@/components/menu/ActionMenu.vue";
import Button from "primevue/button";
import Tooltip from "primevue/tooltip";

export default {
  name: "SelectionBar",
  components: {
    PrimeButton: Button,
    ActionMenu,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    totalItems: {
      type: Number,
      default: null,
    },
    selectAllHandler: {
      type: Function,
      default: null,
    },
    actions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["reload", "reset-page-selection"],
  setup(props, { emit }) {
    const selectionStore = useSelectionStore();
    const navigationStore = useNavigationStore();
    const screenStore = useScreenStore();

    const {
      nodes,
      files,
      folders,
      isCurrentPageFullySelected,
      isSelectionRemovalVisible,
    } = storeToRefs(selectionStore);
    const { pagination, currentNode } = storeToRefs(navigationStore);
    const { isSmallScreen } = storeToRefs(screenStore);

    const moreMenu = ref(null);
    const menuActionItems = ref([]);

    // Computed
    const selectedCount = computed(() => nodes.value.length);
    const totalItemsComputed = computed(
      () => props.totalItems ?? pagination.value?.totalItems ?? 0,
    );

    const actionContext = computed(() => ({
      nodes: nodes.value,
      files: files.value,
      folders: folders.value,
    }));

    const isAllSelected = computed(() => {
      return (
        selectedCount.value === totalItemsComputed.value &&
        totalItemsComputed.value > 0
      );
    });

    const pageItemCount = computed(() => pagination.value?.count ?? 0);

    const showSelectAll = computed(() => {
      if (isAllSelected.value) {
        return false;
      }
      if (isSelectionRemovalVisible.value) {
        return false;
      }
      return (
        selectedCount.value >= pageItemCount.value &&
        pageItemCount.value > 0 &&
        selectedCount.value < totalItemsComputed.value
      );
    });

    // Filter visible actions
    const visibleActions = computed(() => {
      const ctx = actionContext.value;
      return props.actions.filter((action) => {
        if (typeof action.visible === "function") {
          return action.visible(ctx);
        }
        return action.visible !== false;
      });
    });

    // Sort actions by group order, then by individual order
    const sortedActions = computed(() => {
      const actions = [...visibleActions.value];
      // Determine group order from the first order encountered in each group
      const groupOrders = {};
      for (const action of actions) {
        const group = action.group || "__default__";
        if (!(group in groupOrders)) {
          groupOrders[group] = action.order ?? 999;
        }
      }
      actions.sort((a, b) => {
        const groupA = a.group || "__default__";
        const groupB = b.group || "__default__";
        const groupOrderDiff =
          (groupOrders[groupA] ?? 999) - (groupOrders[groupB] ?? 999);
        if (groupOrderDiff !== 0) {
          return groupOrderDiff;
        }
        return (a.order ?? 999) - (b.order ?? 999);
      });
      return actions;
    });

    // Inline actions: display "inline" or "both" (desktop only)
    const inlineActions = computed(() => {
      const actions = sortedActions.value.filter(
        (a) => a.display === "inline" || a.display === "both" || !a.display,
      );
      // Insert group separators
      const result = [];
      let lastGroup = null;
      for (const action of actions) {
        const group = action.group || "__default__";
        if (lastGroup !== null && group !== lastGroup) {
          result.push({ _groupSeparator: true });
        }
        result.push(action);
        lastGroup = group;
      }
      return result;
    });

    // Menu-only actions (desktop): display "menu"
    const menuActions = computed(() => {
      return sortedActions.value.filter((a) => a.display === "menu");
    });

    // All actions for mobile menu (everything goes into the menu)
    const allMobileMenuActions = computed(() => {
      return sortedActions.value;
    });

    // Helper functions
    const resolveStringOrFn = (value, ctx) => {
      if (typeof value === "function") {
        return value(ctx);
      }
      return value || "";
    };

    const resolveDisabled = (action) => {
      if (typeof action.disabled === "function") {
        return action.disabled(actionContext.value);
      }
      return action.disabled === true;
    };

    // Build menu items in ActionMenu format (grouped)
    const buildMenuItems = (actions) => {
      const ctx = actionContext.value;
      const groupMap = {};
      const groupOrder = [];

      for (const action of actions) {
        const group = action.group || "__default__";
        if (!groupMap[group]) {
          groupMap[group] = [];
          groupOrder.push(group);
        }
        const isDisabled =
          typeof action.disabled === "function"
            ? action.disabled(ctx)
            : action.disabled === true;

        groupMap[group].push({
          icon: resolveStringOrFn(action.icon, ctx),
          label: action.label || resolveStringOrFn(action.tooltip, ctx),
          disabled: isDisabled,
          visible: true,
          command: () => action.handler(ctx),
        });
      }

      return groupOrder.map((group) => ({
        items: groupMap[group],
      }));
    };

    const toggleMoreMenu = (event) => {
      const actions = isSmallScreen.value
        ? allMobileMenuActions.value
        : menuActions.value;
      menuActionItems.value = buildMenuItems(actions);
      moreMenu.value.getActionMenuRef().toggle(event);
    };

    const selectAllNodes = async () => {
      if (props.selectAllHandler) {
        props.selectAllHandler();
        return;
      }
      const nodeId = currentNode.value.guid || currentNode.value.id;
      try {
        const response = await alfrescoNodeService.getChildren(
          nodeId,
          totalItemsComputed.value,
          0,
          true,
        );
        if (response.children?.length > 0) {
          selectionStore.selectNodes(response.children);
        }
        selectionStore.setIsCurrentPageFullySelected(false);
        selectionStore.setIsSelectionRemovalVisible(true);
      } catch (error) {
        console.error("Error selecting all nodes:", error);
      }
    };

    const clearSelection = () => {
      selectionStore.resetSelection();
      selectionStore.setIsCurrentPageFullySelected(false);
      selectionStore.setIsSelectionRemovalVisible(false);
      emit("reset-page-selection");
    };

    return {
      // Refs
      moreMenu,
      menuActionItems,
      // State
      nodes,
      files,
      folders,
      isSmallScreen,
      isSelectionRemovalVisible,
      // Computed
      selectedCount,
      totalItemsComputed,
      actionContext,
      showSelectAll,
      isAllSelected,
      inlineActions,
      menuActions,
      allMobileMenuActions,
      // Methods
      resolveStringOrFn,
      resolveDisabled,
      toggleMoreMenu,
      selectAllNodes,
      clearSelection,
    };
  },
};
</script>

<style lang="scss" scoped>
.selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-grow: 1;
}

.selection-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.selection-text {
  font-weight: 500;
  color: var(--p-text-color);
}

.select-all-link {
  font-weight: 600;
  color: var(--p-primary-color);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--p-primary-hover-color);
  }
}

.selection-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.action-separator {
  width: 1px;
  height: 1.5rem;
  background-color: var(--p-surface-border);
  margin: 0 0.5rem;
}

// Mobile styles
.selection-bar.is-mobile {
  flex-direction: column;
  padding: 0.5rem 0.75rem;
  gap: 0.5rem;
}

.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 0.5rem;
}

.mobile-selection-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .selection-icon {
    font-size: 1.125rem;
    color: var(--p-primary-color);
  }

  .selection-text {
    font-size: 0.875rem;
    font-weight: 500;
  }
}

.mobile-selection-actions {
  display: flex;
  align-items: center;

  .select-all-link {
    font-size: 0.875rem;
    white-space: nowrap;
  }
}

.mobile-actions {
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 0.25rem;
  width: 100%;
  padding-top: 0.5rem;
  border-top: 1px solid var(--p-surface-border);
}
</style>
